import { cva } from 'class-variance-authority';
import { cn } from '../utils';
import { applyAmountPrecision, formatReceivedAmount } from '../lib';
import {
  DEFAULT_BASE_ASSET,
  HIDDEN_BALANCES_FULL_TEXT,
  NUMERIC_CONFIG,
} from '@/shared/config';

const balanceDisplayVariants = cva('flex items-end gap-[0.4rem]');

const integerVariants = cva('mb-[-0.4rem] mr-[-0.3rem] leading-none', {
  variants: {
    size: {
      default: 'hero2',
      small: 'headline2',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

const decimalVariants = cva('leading-none', {
  variants: {
    size: {
      default: 'hero5',
      small: 'headline5',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

const currencyVariants = cva('leading-none', {
  variants: {
    size: {
      default: 'hero5',
      small: 'headline5',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

interface BalanceDisplayProps {
  isBalancesHidden: boolean;
  balance: number;
  precision: number;
  currency?: string;
  className?: string;
  size?: 'default' | 'small';
}

const BalanceDisplay = ({
  isBalancesHidden,
  balance,
  precision,
  currency = DEFAULT_BASE_ASSET,
  className = '',
  size = 'default',
}: BalanceDisplayProps) => {
  const roundedBalance = applyAmountPrecision(balance, precision, 'credit');
  const displayBalanceString = formatReceivedAmount(roundedBalance);
  const [integer, decimal] = (
    isBalancesHidden ? HIDDEN_BALANCES_FULL_TEXT : displayBalanceString
  ).split(NUMERIC_CONFIG.decimal.display);

  return (
    <div className={cn(balanceDisplayVariants(), className)}>
      <div className="flex items-end">
        <span className={integerVariants({ size })}>{integer}</span>
        {decimal && precision > 0 && (
          <>
            <span className={cn(decimalVariants({ size }), 'ml-[0.3rem]')}>
              ,
            </span>
            <span className={decimalVariants({ size })}>{decimal}</span>
          </>
        )}
      </div>
      {currency && (
        <>
          <span className={currencyVariants({ size })}> </span>
          <span className={currencyVariants({ size })}>{currency}</span>
        </>
      )}
    </div>
  );
};

export { BalanceDisplay };
